<template>
  <div>
    <div class="fixed-main-box">
      <!-- Schedule Form -->
      <b-row class="mb-3" v-if="!viewOnly">
        <b-col cols="12" class="pb-2">
          <h4><strong>Schedule Message</strong></h4>
        </b-col>
        <b-col v-if="!sendAtText.error" cols="12" class="d-flex justify-content-start align-items-center">
          <div>
            This message will be sent at
          </div>
          <div style="width: 100px;" class="ml-2">
            <b-form-select v-model="relativeSendAtForm.atHour" :options="hourOptions" />
          </div>
          <div class="ml-2">:</div>
          <div style="width: 100px;" class="ml-2">
            <b-form-select v-model="relativeSendAtForm.atMinute" :options="minuteOptions" />
          </div>
          <div class="ml-2" v-html="sendAtText.text">
          </div>
        </b-col>
        <b-col v-else cols="12">
          <div v-html="sendAtText.text"></div>
        </b-col>
      </b-row>

      <!-- Main Form -->
      <b-row class="mt-4">
        <b-col cols="12">
          <h4><strong>Message Delivery</strong></h4>
        </b-col>
        <b-col v-if="isScheduled && !viewOnly" cols="12">
          <b-alert show>You cannot edit this message, as it is either delivered or in progress.</b-alert>
        </b-col>
        <b-col cols="12">
          <advanced-form
            autocomplete="off"
            :hideFormControl="true"
            :form="formFields"
            v-bind="form"
            v-model="form"
            :disabled="viewOnly"
          >

            <b-alert
              v-if="(!Array.isArray(syncedEmailAddresses) || !syncedEmailAddresses.length) && !viewOnly"
              slot="from-before"
              variant="warning"
              show
            >
              It seems like no email addresses have been connected to Real Links yet. In order for us to send emails on your behalf, you need to go to <a :href="$router.resolve({ name: 'EmailSetup' }).href" target="_blank">Settings > Connect Email</a> page.
            </b-alert>
          </advanced-form>
        </b-col>
      </b-row>

      <!-- Input Form with preview -->
      <b-row class="mb-3">
        <b-col cols="12">
          <h4 class="pt-3 pb-2"><strong>Message Content</strong></h4>
        </b-col>
        <b-col cols="12" lg="7">
          <content-preview
            class="mb-3"
            :campaign="campaign"
            :fromEmail="form.from"
            :job="inputForm.job || null"
            :contentShare="inputForm.contentShare || null"
            :jobs="inputForm.jobs || null"
            :contentShares="inputForm.contentShares || null"
            :collectiveGoal="collectiveGoal"
            :inputs="inputForm"
            :body="body"
            @sendTestMessage="() => { if (!viewOnly) { $bvModal.show('send-test-message-modal') } }"
          />
          <div class="mt-3 text-muted">Please note that the display below is just an estimated layout. Use the "Send Test" button to get an accurate preview.</div>
        </b-col>
        <b-col cols="12" lg="5">
          <advanced-form
            autocomplete="off"
            :hideFormControl="true"
            :form="inputFormFields"
            :disabled="viewOnly"
            v-bind="inputForm"
            v-model="inputForm"
          />
        </b-col>
      </b-row>

      <!-- Variables that can be used -->
      <b-row>
        <b-col>
          <b-card no-body class="mb-1">
            <b-card-header class="d-flex justify-content-between align-items-center bg-white">
              <div @click="displayVariables = !displayVariables" v-b-toggle.available-variables-card>
                <strong>Template variables you can use</strong>
              </div>
              <b-button @click="displayVariables = !displayVariables"  v-b-toggle.available-variables-card variant="outline-primary" class="px-2" size="sm">
                <font-awesome-icon v-if="displayVariables" icon="chevron-up" size="sm"/>
                <font-awesome-icon v-else icon="chevron-down" size="sm"/>
              </b-button>
            </b-card-header>
            <b-collapse id="available-variables-card" accordion="available-variables-card" role="tabpanel" class="p-3">
              <b>User related variables</b> you can use:<br>
              <span v-for="{ key, description } in variables.filter(({ type }) => type === 'USER')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
              <b>Job related variables</b> you can use:<br>
              <span v-for="{ key, description } in variables.filter(({ type }) => type === 'JOB')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
              <b>Leaderboard related variables</b> you can use:<br>
              <span v-for="{ key, description } in variables.filter(({ type }) => type === 'LEADERBOARD')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
            </b-collapse>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <div v-if="!viewOnly" class="fixed-buttons-box border-top d-flex justify-content-between align-items-center">
      <!-- Delete Button -->
      <b-link
        v-if="!isScheduled"
        class="text-danger"
        @click="$bvModal.show('delete-email-message')"
      >Delete</b-link>

      <!-- Save Button -->
      <b-button
        variant="primary"
        @click="input('emailMessage')"
        class="float-right"
        :disabled="isScheduled || isSceduledInPast"
        pill
      >
        Save Message
      </b-button>
    </div>

    <!-- Test Email Form -->
    <b-modal id="send-test-message-modal" title="Send Test Message" :hide-footer="true">
      <b-row>
        <b-col>
          <b-form>
            <!-- Email Addresses Field -->
            <test-message-form
              channel="email"
              :body="body"
              :subject="form.subject"
              :from="form.from"
              :job="inputForm.job"
              :contentShare="inputForm.contentShare"
              :jobs="inputForm.jobs"
              :contentShares="inputForm.contentShares"
              :collectiveGoal="collectiveGoal"
              :isIndividual="isIndividual"
              :campaignID="campaignID"
              :campaignComponentID="campaignComponentID"
              :preferences="campaign.preferences"
              :inputs="inputs"
              :template="template.id"
              :templateDefinition="JSON.stringify(template)"
            />
          </b-form>
        </b-col>
      </b-row>
    </b-modal>

    <!-- ALERT MODAL -->
    <b-modal id="target-group-change" title="Target group has changed" ok-only ok-title="I Understand">
      <div v-if="isIndividual">
        <p>This email will be sent as a direct message to everyone in the selected group.</p>
      </div>
      <div v-else>
        <p>This email will be sent as a group message to everyone in the selected group.</p>
      </div>
    </b-modal>

    <!-- DELETE MODAL -->
    <b-modal
      v-if="!isScheduled"
      id="delete-email-message"
      title="Delete Email Message"
      ok-title="Delete"
      cancel-title="Cancel"
      @ok="$emit('delete', { moveComponents })"
      cancel-variant="light"
      ok-variant="danger"
    >
      <p>Are you sure you want to delete this email message?</p>
      <b-form-checkbox v-model="moveComponents" name="check-button" switch>
        Move all the following messages forward
      </b-form-checkbox>
    </b-modal>
  </div>
</template>

<script>
import AdvancedForm from '@src/components/AdvancedForm';
import emailTemplates from '@src/views/campaign-admin/templates/emails';
import Share from '@src/components/Share';
import gridMixin from '@src/mixins/grid';
import ContentPreview from '@src/components/campaign-admin/message-previews/emails/content';
import TestMessageForm from '@src/components/campaign-admin/test-message-form.vue';
import { mapRoot, mapPreferences } from '@src/mixins/root';
import updateMessageMixin from '@/mixins/updateMessage';

export default {
  components: {
    AdvancedForm,
    Share,
    TestMessageForm,
    ContentPreview,
  },
  data() {
    const { emailMessage } = this.value || {};
    console.log({ emailMessage });
    delete emailMessage.__typename;
    const { template, inputs, subject, body, job, contentShare, jobs, contentShares, relativeSendAt, ...rest } = emailMessage || {};
    return {
      moveComponents: false,
      form: {
        from: null,
        to: 'ALL_USERS',
        sendAt: '',
        status: 'DRAFT',
        subject: subject || '',
        ...(rest || {})
      },
      relativeSendAt: {
        ...(relativeSendAt || {})
      },
      relativeSendAtForm: {
        atHour: relativeSendAt.atHour || 12,
        atMinute: relativeSendAt.atMinute || 0,
      },
      templateID: template || null,
      isScheduled: false,
      inputForm: {
        ...Object.keys(inputs || {}).reduce((acc, key) => {
          if (key.includes('IMAGE')) {
            try {
              acc[key] = JSON.parse(inputs[key]);
            } catch (e) {
              console.log(e);
              acc[key] = inputs[key];
            }
          } else {
            acc[key] = inputs[key];
          }
          return acc;
        }, {}),
        ...(job ? { job } : {}),
        ...(contentShare ? { contentShare } : {}),
        ...(jobs ? { jobs } : {}),
        ...(contentShares ? { contentShares } : {})
      },
      displayVariables: false,
    };
  },
  mixins: [
    gridMixin,
    updateMessageMixin
  ],
  computed: {
    ...mapPreferences([ 'featuresEnabled' ]),
    ...mapRoot([ 'isInMobi' ]),
    hasContentSharesV2Enabled() {
      return (this.featuresEnabled || []).includes('content-share-v2');
    },
    template() {
      const { templateID, isInMobi } = this;

      let templateFolder = null;
      if (isInMobi) {
        templateFolder = 'inMobi';
      }

      return emailTemplates(templateFolder).find(({ id }) => id === templateID);
    },
    toChoices() {
      const { isIndividual } = this;
      return {
        ALL_USERS: `All Participants: Everyone in the campaign - ${isIndividual ? 'sent as a direct email' : 'sent in a group email'}`,
        REGISTERED_USERS: `Active Participants: Those who have engaged with the campaign - ${isIndividual ? 'sent as a direct email' : 'sent in a group email'}`,
        INVITED_USERS: `Inactive Participants: Those who have not engaged with the campaign yet - ${isIndividual ? 'sent as a direct email' : 'sent in a group email'}`
      };
    },
    formFields() {
      const { syncedEmailAddresses } = this;
      const defaultEmailOption = { NONE: '---- Select an email address ----' };
      return {
        from: {
          type: 'SELECT',
          label: 'From',
          disabled: !Array.isArray(syncedEmailAddresses) || !syncedEmailAddresses.length || this.isScheduled,
          choices: Array.isArray(syncedEmailAddresses) && syncedEmailAddresses.length
            ? syncedEmailAddresses.reduce((out, { emailAddress, fullName }) => {
              out[emailAddress] = `${emailAddress} (${fullName})`;
              return out;
            }, defaultEmailOption) : [ defaultEmailOption ],
          helpText: 'The person this message will be delivered on behalf of'
        },
        to: {
          label: 'To',
          type: 'SELECT',
          required: true,
          disabled: this.isScheduled,
          choices: this.toChoices,
          helpText: 'The target group of this email'
        },
        subject: {
          label: 'Subject',
          type: 'STRING',
          required: true,
          disabled: this.isScheduled,
          helpText: 'Subject of this email'
        },
        status: {
          label: 'Status',
          type: 'SELECT',
          required: true,
          disabled: this.isScheduled,
          choices: {
            READY: 'Ready',
            DRAFT: 'Draft',
          },
        },
      };
    },
    inputFormFields() {
      const { template, jobsWhere, contentSharesWhere, campaign } = this;
      const { preferences } = campaign || {};
      const { jobRelevancy, contentRelevancy } = preferences || {};
      console.log({ template, jobsWhere, contentSharesWhere, jobRelevancy, contentRelevancy });
      const inputFields = template ? template.inputs : {};
      return {
        ...(this.isJob && (jobRelevancy === 'MANUAL' || !jobRelevancy) ? {
          job: {
            label: 'Job',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: jobsWhere || {
                status: 'ACTIVE',
              },
              many: false,
              order: 'CREATED_DESC',
            },
            helpText: 'Select the job you want to include in the email',
          },
        } : {}),
        ...(this.areJobs && (jobRelevancy === 'MANUAL' || !jobRelevancy) ? {
          jobs: {
            label: 'Jobs',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: jobsWhere || {
                status: 'ACTIVE',
              },
              many: true,
              order: 'CREATED_DESC',
            },
            helpText: 'Select up to 4 jobs you want to include in the email',
          },
        } : {}),
        ...(this.isContent && (contentRelevancy === 'MANUAL' || !contentRelevancy) ? {
          contentShare: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: false,
              where: contentSharesWhere || {
                status: 'ACTIVE',
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select the piece of content you want to include in the email',
          },
        } : {}),
        ...(this.arePiecesOfContent && (contentRelevancy === 'MANUAL' || !contentRelevancy) ? {
          contentShares: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: true,
              where: contentSharesWhere || {
                id: 'NONE',
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select up to 4 pieces of content you want to include in the email',
          },
        } : {}),
        ...inputFields,
      };
    },
    body() {
      const { template, inputForm, hasContentSharesV2Enabled } = this;
      if (!template) return null;
      const { body, blocks } = template;
      if (body && typeof body === 'string') {
        let replacedBody = body;
        const { jobs, contentShares } = inputForm;
        const blockKeys = blocks ? Object.keys(blocks) : null;
        console.log({ blocks, blockKeys, jobs, contentShares });
        if (blocks && Array.isArray(blockKeys) && (Array.isArray(jobs) || Array.isArray(contentShares))) {
          blockKeys.forEach(blockKey => {
            const block = blocks[blockKey];
            let items = [];
            if (blockKey === 'BLOCK_JOB') {
              items = jobs;
            }
            if (blockKey === 'BLOCK_CONTENT') {
              items = contentShares;
            }
            const replacedHTML = items
              .map(({ id, customFields }) => {
                if (typeof block === 'function') {
                  const postType = (customFields && customFields.displayContent) || 'link';
                  console.log({ postType });
                  const contentType = hasContentSharesV2Enabled && [ 'image', 'video', 'document' ].includes(postType) ? 'linkedin-only' : 'default';
                  console.log({ contentType });
                  return block(contentType).replaceAll('[ID]', id);
                }
                return block.replaceAll('[ID]', id);
              })
              .join('');
            console.log({ replacedHTML });
            replacedBody = replacedBody.replaceAll(`<${blockKey}>`, replacedHTML);
          });
          console.log({ replacedBody });
        }
        Object.keys(inputForm).forEach(key => {
          let value = inputForm[key];
          if (value && typeof value === 'object') {
            value = value.fileLocation;
          }
          replacedBody = replacedBody.replaceAll(`[[${key}]]`, value);
        });
        return replacedBody;
      };
      let replacedBody = JSON.stringify(body);
      Object.keys(inputForm).forEach(key => {
        // console.log({ key, value: inputForm[key], tunrDown: turndownService.turndown(inputForm[key]).replaceAll('\\', '') });
        replacedBody = replacedBody.replaceAll(`[[${key}]]`);
      });
      console.log({ replacedBody });
      return replacedBody;
    },
    isIndividual() {
      return true;
    },
  }
};
</script>

<style scoped>
.fixed-buttons-box {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  padding: 8px 10px 8px 10px;
  border-top: 1px solid #e9ecef;
  z-index: 1000;
}
.fixed-main-box {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 60px;
  padding: 20px;
  overflow-y: scroll;
}
</style>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-email-message-relative-dates.vue